import { getAuthToken, getSelectedClientInformation } from 'utilities/authUtils';
import Log from 'utilities/log';
import Symbol from 'es6-symbol';
import auth from 'auth/auth';

import { SET_SELF_SERVICE_ERROR_EVENT_ID } from './apiError.types';

export const SELF_SERVE_API_BASE_URL = process.env.REACT_APP_SELF_SERVE_API_URL|| 'http://localhost:3001/';

function callApi(token, endpoint, authenticated, method = 'GET', payload) {
  const clientId = getSelectedClientInformation().id;
  const baseUrl = `${SELF_SERVE_API_BASE_URL}v1/client/${clientId}/`;
  Log.trace('Requesting', endpoint);

  // include credentials
  // let config = { headers: {}, credentials: "include" };

  // no credentials
  let config = { headers: {} };

  if (authenticated) {
    Log.trace('Auth required');
    if (token) {
      config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    } else {
      Log.error('No token available.');
      throw new Error('No token saved!');
    }
  }
  config.method = method;
  if (typeof payload !== 'undefined') {
    //Is this form data?
    if (Object.prototype.toString.call(payload) === '[object FormData]') {
      config.body = payload;
    } else {
      config.body = JSON.stringify(payload);
      config.headers['Content-Type'] = 'application/json; charset=utf-8';
    }
  }

  Log.trace(`Fetching ${baseUrl + endpoint}`);
  // add cookie to request
  return fetch(baseUrl + endpoint, config)
    .then((response) => {
      if (response.status > 399) {
        Log.error(`Error during fetch to ${baseUrl + endpoint}: ${response.status}`);
      }
      return response.json()
        .then((json) => {
          Log.trace(`Response body for ${baseUrl + endpoint}: ${JSON.stringify(json)}`);
          return ({ json, response });
        })
        .catch((e) => {
          return ({
            json: [],
            response,
          });
        });
    })
    .then(({ json, response }) => {
      if (response.status === 401) {
        return Promise.reject({ authenticationRequired: true });
      }
      if (response.status === 409) {
        return Promise.reject({ isCollision: true, ...json });
      }
      if (!response.ok) {
        return Promise.reject(json);
      }
      let metadata = {};
      if (response.headers.get('X-Total-Count')) {
        metadata.totalCount = Number(response.headers.get('X-Total-Count'));
      }
      return { json, metadata };
    });
}

export const CALL_SELF_SERVE_API = Symbol('Call Benefit API');

export default ({ dispatch, getState }) => (next) => (action) => {
  const callAPI = action[CALL_SELF_SERVE_API];

  // So the middleware doesn't get applied to every single action
  if (typeof callAPI === 'undefined') {
    return next(action);
  }

  let { endpoint, types, authenticated, method, payload, options, context } = callAPI;

  const [requestType, successType, errorType] = types;

  next({
    type: requestType,
    context,
    payload,
  });

  let jwt;
  if (authenticated) {
    try {
      jwt = getAuthToken();
    } catch (e) {
      Log.trace('ERROR GETTING TOKEN', e);
    }
  }

  // Passing the authenticated boolean back in our data will let us distinguish
  // between normal and secret quotes
  return callApi(jwt, endpoint, authenticated, method, payload).then(
    (response) => {
      return next({
        response: response.json,
        metadata: response.metadata,
        authenticated,
        payload,
        type: successType,
        options,
        context,
      });
    },
    (error) => {
      const resp = {
        type: errorType,
        context,
        isCollision: error.isCollision,
      };
      if (error.messages) {
        resp.messages = error.messages;
      } else if (error.message) {
        resp.messages = [error.message];
      } else {
        resp.messages = ['There was an error.'];
      }

      dispatch({ type: SET_SELF_SERVICE_ERROR_EVENT_ID, payload: error.eventId });

      Log.error(`API Error: ${JSON.stringify(resp)}`);
      if (error.authenticationRequired) {
        auth.renewSession();
      }
      return next(resp);
    },
  );
};